/**
 * Tool Output Summary
 *
 * Builds compact model-facing summaries for structured tool results.
 *
 * Primary responsibilities:
 * - Preserve follow-up identifiers such as Space IDs
 * - Avoid feeding raw JSON blobs back into model context
 *
 * Dependencies:
 * - Assistant visible content sanitizer
 */

import { sanitizeAssistantVisibleContent } from '@/lib/chat/assistant-visible-content'

const MODEL_DETAIL_MESSAGE = 'Use these returned details; do not rely on UI-only tool cards.'

const collectionPriority = [
  'rows',
  'results',
  'events',
  'eventDefinitions',
  'event_definitions',
  'spaces',
  'folders',
  'files',
  'tasks',
  'notes',
  'sheets',
  'sprints',
  'events',
  'collections',
  'members',
  'memberDirectory',
  'chats',
  'conversations',
  'messages',
  'whiteboards',
  'designs',
  'items',
  'columns'
]

const scalarPriority = [
  'message',
  'status',
  'operation',
  'kind',
  'summary',
  'stdout',
  'stderr',
  'exitCode',
  'exit_code',
  'text'
]

const nestedResultPriority = [
  'result',
  'data',
  'payload',
  'response',
  'output',
  'structuredContent',
  'structured_content',
  'table'
]

const itemFields = [
  'id',
  'key',
  'rowNumber',
  'rowIndex',
  'address',
  'columnName',
  'columnIndex',
  'name',
  'label',
  'title',
  'displayName',
  'space_id',
  'spaceId',
  'workspace_id',
  'workspaceId',
  'folder_id',
  'folderId',
  'task_id',
  'taskId',
  'note_id',
  'noteId',
  'env',
  'envName',
  'env_name',
  'variable',
  'variableName',
  'variable_name',
  'email',
  'status',
  'type',
  'present',
  'length',
  'count',
  'content_type',
  'contentType',
  'path',
  'publishLevel',
  'previewUrl',
  'downloadUrl',
  'url'
]

const longTextFields = ['content', 'body', 'text', 'description']
const scalarArrayFields = ['values']

type ToolRecord = Record<string, unknown>

const asRecord = (value: unknown): ToolRecord | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as ToolRecord)
    : null

const readScalar = (value: unknown) => {
  if (typeof value === 'string') return sanitizeAssistantVisibleContent(value)
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return null
}

function formatScalarSection(key: string, value: unknown) {
  const scalar = readScalar(value)

  if (!scalar) return null

  if (
    key === 'stdout' ||
    key === 'stderr' ||
    key === 'output' ||
    key === 'text'
  ) {
    return `${key}:\n${scalar}`
  }

  return `${key}: ${scalar}`
}

function summarizeRecordItem(
  value: unknown
) {
  const record = asRecord(value)

  if (!record) {
    return readScalar(value)
  }

  const parts = itemFields
    .map((field) => {
      const scalar = readScalar(record[field])
      return scalar ? `${field}=${scalar}` : null
    })
    .filter((part): part is string => Boolean(part))

  for (const field of longTextFields) {
    if (itemFields.includes(field)) continue

    const scalar = readScalar(record[field])
    if (!scalar) continue

    parts.push(`${field}:\n${scalar}`)
  }

  for (const field of scalarArrayFields) {
    const values = record[field]
    if (!Array.isArray(values)) continue

    const readableValues = values
      .map(readScalar)
      .filter((value): value is string => value !== null)

    if (readableValues.length === 0) continue

    parts.push(`${field}=[${readableValues.join(' | ')}]`)
  }

  if (parts.length > 0) return parts.join('; ')

  const keys = Object.keys(record)
  return keys.length > 0 ? `fields=${keys.join(', ')}` : null
}

function summarizeContentItems(value: unknown) {
  if (!Array.isArray(value)) return null

  if (value.length === 0) {
    return 'content: none'
  }

  const lines = value
    .map((item, index) => {
      const record = asRecord(item)

      if (!record) {
        const scalar = readScalar(item)
        return scalar
          ? `${index + 1}. ${scalar}`
          : null
      }

      const type = readScalar(record.type)
      const text = readScalar(record.text) ?? readScalar(record.content)

      if (text) {
        const prefix = type ? `type=${type}; ` : ''
        return `${index + 1}. ${prefix}text:\n${text}`
      }

      const summary = summarizeRecordItem(record)
      return summary ? `${index + 1}. ${summary}` : null
    })
    .filter((line): line is string => Boolean(line))

  return lines.length > 0
    ? `content (${value.length}):\n${lines.join('\n')}`
    : `content (${value.length})`
}

function summarizeCollection(key: string, value: unknown) {
  if (!Array.isArray(value)) {
    return null
  }

  if (value.length === 0) {
    return `${key}: none`
  }

  const lines = value
    .map((item, index) => {
      const summary = summarizeRecordItem(item)
      return summary ? `${index + 1}. ${summary}` : null
    })
    .filter((line): line is string => Boolean(line))

  return lines.length > 0
    ? `${key} (${value.length}):\n${lines.join('\n')}`
    : `${key} (${value.length})`
}

function summarizeNestedRecord(key: string, value: unknown, depth: number) {
  const record = asRecord(value)

  if (!record || depth >= 3) {
    return null
  }

  const nestedSections = summarizeStructuredResult(record, depth + 1)

  if (nestedSections.length > 0) {
    return `${key}:\n${nestedSections.join('\n')}`
  }

  const summary = summarizeRecordItem(record)
  return summary ? `${key}: ${summary}` : null
}

function summarizeStructuredResult(result: ToolRecord, depth = 0) {
  const sections: string[] = []
  const seenKeys = new Set<string>()

  const error = readScalar(result.error)
  if (error) {
    seenKeys.add('error')
    sections.unshift(`error: ${error}`)
  }

  for (const key of scalarPriority) {
    const section = formatScalarSection(key, result[key])
    if (section) {
      seenKeys.add(key)
      sections.push(section)
    }
  }

  const contentSection = summarizeContentItems(result.content)
  if (contentSection) {
    seenKeys.add('content')
    sections.push(contentSection)
  }

  for (const key of collectionPriority) {
    if (seenKeys.has(key)) continue

    const section = summarizeCollection(key, result[key])
    if (section) {
      seenKeys.add(key)
      sections.push(section)
    }
  }

  for (const [key, value] of Object.entries(result)) {
    if (seenKeys.has(key) || collectionPriority.includes(key)) continue
    const section = summarizeCollection(key, value)
    if (section) {
      seenKeys.add(key)
      sections.push(section)
    }
  }

  for (const key of nestedResultPriority) {
    if (seenKeys.has(key)) continue

    const nested = summarizeNestedRecord(key, result[key], depth)
    if (nested) {
      seenKeys.add(key)
      sections.push(nested)
    }
  }

  for (const key of collectionPriority.map((key) => key.replace(/s$/, ''))) {
    if (seenKeys.has(key)) continue

    const record = asRecord(result[key])
    if (!record) continue

    const summary = summarizeRecordItem(record)
    if (summary) {
      seenKeys.add(key)
      sections.push(`${key}: ${summary}`)
    }
  }

  return sections
}

// GitHub read-tool passthrough: render the large text fields (content, diff,
// comparison, commits, snippets) verbatim, bounded so an enormous diff/file
// cannot blow the context window. Returns null when the record has none of
// these (e.g. a list_repos/list_issues result -> generic summarizer handles it).
const GITHUB_MAX_CHARS = 24_000

function boundedGithubText(value: string): string {
  if (value.length <= GITHUB_MAX_CHARS) return value
  return `${value.slice(0, GITHUB_MAX_CHARS)}\n... [truncated ${value.length - GITHUB_MAX_CHARS} chars]`
}

function surfaceGithubReadResult(record: ToolRecord): string | null {
  // get_file: { path, content, startLine?, endLine?, truncated? }
  if (typeof record.content === 'string') {
    const path = typeof record.path === 'string' ? record.path : 'file'
    const range =
      typeof record.startLine === 'number' && typeof record.endLine === 'number'
        ? ` (lines ${record.startLine}-${record.endLine})`
        : ''
    return boundedGithubText(`${path}${range}:\n\n${record.content}`)
  }

  // get_pull_request_diff: { number?, diff }
  if (typeof record.diff === 'string') {
    const num = typeof record.number === 'number' ? ` #${record.number}` : ''
    return boundedGithubText(`Pull request${num} diff:\n\n${record.diff}`)
  }

  // compare_refs: { comparison } (object with files/patches or a diff string)
  if (record.comparison !== undefined && record.comparison !== null) {
    const comparison =
      typeof record.comparison === 'string'
        ? record.comparison
        : JSON.stringify(record.comparison, null, 2)
    return boundedGithubText(`Comparison:\n\n${comparison}`)
  }

  // list_commits: { commits: [...] } — surface the metadata array verbatim.
  if (Array.isArray(record.commits)) {
    return boundedGithubText(
      `Commits (${record.commits.length}):\n\n${JSON.stringify(record.commits, null, 2)}`
    )
  }

  // search_code single/array result: { snippets: [...] }
  if (Array.isArray(record.snippets)) {
    const path = typeof record.path === 'string' ? record.path : ''
    const header = path ? `${path}:\n` : ''
    return boundedGithubText(`${header}${record.snippets.join('\n---\n')}`)
  }

  // search_code list result: { results: [{ path, snippets }] }
  if (Array.isArray(record.results)) {
    return boundedGithubText(JSON.stringify(record.results, null, 2))
  }

  return null
}

// Knowledge graph reads return node lists (title, type, sourceId, summary) and
// "related items" grouped by edge type. The generic structured summarizer drops
// sourceId/summary and does not expand the nested related.items arrays, so
// surface them explicitly and readably. Bounded so a large graph slice cannot
// blow the context window. Returns null for unrelated shapes.
const KNOWLEDGE_GRAPH_MAX_CHARS = 12_000

interface ReadableGraphNode {
  title?: unknown
  type?: unknown
  sourceId?: unknown
  summary?: unknown
}

function formatGraphNode(value: unknown): string | null {
  const record = asRecord(value)
  if (!record) return null

  const node = record as ReadableGraphNode
  const title = readScalar(node.title)
  const type = readScalar(node.type)
  const sourceId = readScalar(node.sourceId)
  const summary = readScalar(node.summary)

  const parts: string[] = []
  if (title) parts.push(title)
  if (type) parts.push(`(${type})`)
  if (sourceId) parts.push(`[${sourceId}]`)
  const head = parts.join(' ')

  const line = summary ? `${head} — ${summary}` : head
  return line.trim() ? line : null
}

function boundedKnowledgeGraphText(value: string): string {
  if (value.length <= KNOWLEDGE_GRAPH_MAX_CHARS) return value
  return `${value.slice(0, KNOWLEDGE_GRAPH_MAX_CHARS)}\n... [truncated ${value.length - KNOWLEDGE_GRAPH_MAX_CHARS} chars]`
}

function surfaceKnowledgeGraphResult(record: ToolRecord): string | null {
  // queryKnowledgeGraph: { count, nodes: [...] }
  if (Array.isArray(record.nodes)) {
    const count = typeof record.count === 'number' ? record.count : record.nodes.length
    if (record.nodes.length === 0) {
      return 'Knowledge graph nodes (0): none'
    }
    const lines = record.nodes
      .map((node, index) => {
        const formatted = formatGraphNode(node)
        return formatted ? `${index + 1}. ${formatted}` : null
      })
      .filter((line): line is string => Boolean(line))
    return boundedKnowledgeGraphText(`Knowledge graph nodes (${count}):\n${lines.join('\n')}`)
  }

  // relatedItems: { found, root, relatedCount, related: [{ edgeType, items }] }
  if (Array.isArray(record.related)) {
    if (record.found === false) {
      return 'Related items: root node not found in the workspace graph.'
    }

    const rootLine = formatGraphNode(record.root)
    const relatedCount =
      typeof record.relatedCount === 'number' ? record.relatedCount : undefined
    const header = `Related items${
      relatedCount !== undefined ? ` (${relatedCount})` : ''
    } for ${rootLine ?? 'root'}:`

    const groups = record.related
      .map((group) => {
        const groupRecord = asRecord(group)
        if (!groupRecord) return null
        const edgeType = readScalar(groupRecord.edgeType) ?? 'related'
        const items = Array.isArray(groupRecord.items) ? groupRecord.items : []
        const itemLines = items
          .map((item) => formatGraphNode(item))
          .filter((line): line is string => Boolean(line))
          .map((line) => `  - ${line}`)
        return itemLines.length > 0 ? `${edgeType}:\n${itemLines.join('\n')}` : null
      })
      .filter((line): line is string => Boolean(line))

    if (groups.length === 0) {
      return boundedKnowledgeGraphText(`${header}\nNo related items.`)
    }

    return boundedKnowledgeGraphText(`${header}\n${groups.join('\n')}`)
  }

  return null
}

export function summarizeToolOutputForModel(result: unknown, toolName: string) {
  if (toolName === 'set_todos' || toolName === 'update_todo_status') {
    return 'Losi steps updated.'
  }

  if (result === undefined || result === null) {
    return `${toolName.replace(/^[^_]*__/, '')} operation completed successfully`
  }

  if (typeof result === 'string') {
    const sanitizedResult = sanitizeAssistantVisibleContent(result)
    const trimmed = sanitizedResult.trim()

    if (!trimmed) {
      return `${toolName} completed.`
    }

    if (/^[{[]/.test(trimmed)) {
      try {
        return summarizeToolOutputForModel(JSON.parse(trimmed), toolName)
      } catch {
        return `${toolName} returned structured data. ${MODEL_DETAIL_MESSAGE}`
      }
    }

    return sanitizedResult
  }

  const record = asRecord(result)
  if (!record) {
    return `${toolName} returned structured data. ${MODEL_DETAIL_MESSAGE}`
  }

  // Web fetch (FetchDataFromUrl / webScraper__FetchDataFromUrl) returns
  // { url, mdDoc } — the mdDoc field is the actual page content and must
  // reach the model verbatim (truncated only if enormous). The generic
  // structured summarizer below has no rule for this shape and previously
  // dropped mdDoc entirely, so the model saw only "Fields: url, mdDoc".
  if (
    (toolName === 'FetchDataFromUrl' || toolName.endsWith('__FetchDataFromUrl')) &&
    typeof record.mdDoc === 'string'
  ) {
    const url = typeof record.url === 'string' ? record.url : ''
    const body = record.mdDoc as string
    const header = url ? `Fetched: ${url}\n\n` : ''
    return sanitizeAssistantVisibleContent(header + body)
  }

  if (record.setupCard || record.setup_card || record.skillSetup || record.skill_setup) {
    return 'Tool setup is required. A setup card was shown to the user.'
  }

  // MCP tool calls can still arrive as a JSON-RPC envelope
  // ({ jsonrpc, id: 'losi-tools-call', result }). Unwrap before summarizing so
  // the model sees the actual tools/call payload, not the request id.
  if (
    (toolName === 'callMcpServerTool' || toolName.endsWith('__callMcpServerTool')) &&
    typeof record.result === 'object' &&
    record.result !== null &&
    !Array.isArray(record.result)
  ) {
    const nested = record.result as Record<string, unknown>
    if ('jsonrpc' in nested || nested.id === 'losi-tools-call') {
      return summarizeToolOutputForModel(
        {
          ...record,
          result: 'result' in nested ? nested.result : nested
        },
        toolName
      )
    }
  }

  // Open-Meteo getWeather returns nested current/hourly/daily — the generic
  // summarizer only lists field names ("Fields: latitude, current, …") so the
  // model never sees temperature. Surface current temps + condition explicitly.
  if (toolName === 'getWeather' || toolName.endsWith('__getWeather')) {
    const current = asRecord(record.current)
    const tempC =
      typeof current?.temperature_2m === 'number'
        ? current.temperature_2m
        : typeof record.temperature_c === 'number'
          ? record.temperature_c
          : typeof current?.temp_c === 'number'
            ? current.temp_c
            : null
    const weatherCode =
      typeof current?.weather_code === 'number'
        ? current.weather_code
        : typeof record.weather_code === 'number'
          ? record.weather_code
          : null
    const timezone =
      typeof record.timezone === 'string'
        ? record.timezone
        : typeof record.location === 'string'
          ? record.location
          : ''
    const wmoLabels: Record<number, string> = {
      0: 'Clear sky',
      1: 'Mainly clear',
      2: 'Partly cloudy',
      3: 'Overcast',
      45: 'Fog',
      51: 'Light drizzle',
      61: 'Slight rain',
      63: 'Moderate rain',
      65: 'Heavy rain',
      71: 'Slight snow',
      80: 'Slight rain showers',
      95: 'Thunderstorm'
    }
    const condition =
      typeof record.summary === 'string'
        ? record.summary
        : typeof record.condition === 'string'
          ? record.condition
          : weatherCode !== null && wmoLabels[weatherCode]
            ? wmoLabels[weatherCode]
            : weatherCode !== null
              ? `WMO code ${weatherCode}`
              : ''
    const parts = [
      timezone ? `Location/timezone: ${timezone}` : null,
      tempC !== null ? `Temperature: ${tempC.toFixed(1)}°C` : null,
      condition ? `Conditions: ${condition}` : null,
      typeof record.latitude === 'number' && typeof record.longitude === 'number'
        ? `Coordinates: ${record.latitude}, ${record.longitude}`
        : null
    ].filter((line): line is string => Boolean(line))

    if (parts.length > 0) {
      return sanitizeAssistantVisibleContent(
        [`Weather:`, ...parts, MODEL_DETAIL_MESSAGE].join('\n')
      )
    }
  }

  // createPdfDocument returns { success, url, fileName, message, pdfBase64? }.
  // The generic summarizer keeps message but drops url/fileName, so surface the
  // download link explicitly for this tool.
  if (toolName === 'createPdfDocument' || toolName.endsWith('__createPdfDocument')) {
    const url = typeof record.url === 'string' ? record.url : ''
    const fileName = typeof record.fileName === 'string' ? record.fileName : 'document.pdf'
    const message = typeof record.message === 'string' ? record.message : ''
    const parts = [
      url ? `Download URL: ${url}` : 'No download URL (storage upload failed).',
      `File name: ${fileName}`,
      ...(message ? [message] : [])
    ]
    return sanitizeAssistantVisibleContent(parts.join('\n'))
  }

  // requestSignature returns { success, requestId, signUrl, message }. The
  // generic summarizer keeps message but drops signUrl/requestId, so surface
  // the shareable signing link explicitly for this tool.
  if (toolName === 'requestSignature' || toolName.endsWith('__requestSignature')) {
    const signUrl = typeof record.signUrl === 'string' ? record.signUrl : ''
    const requestId = typeof record.requestId === 'string' ? record.requestId : ''
    const message = typeof record.message === 'string' ? record.message : ''
    const parts = [
      signUrl ? `Signing link: ${signUrl}` : 'No signing link (request could not be created).',
      ...(requestId ? [`Request id: ${requestId}`] : []),
      ...(message ? [message] : [])
    ]
    return sanitizeAssistantVisibleContent(parts.join('\n'))
  }
  // for user-uploaded files and generic virtual files) return a metadata
  // envelope (id, source, fileId, path, name, scope, mimeType, tokens, ...)
  // ahead of the actual `content` field. Ensure the content field is
  // returned verbatim.
  if (
    (toolName === 'losi__fs_read_file' || toolName.endsWith('__losi__fs_read_file')) &&
    typeof record.content === 'string'
  ) {
    const label =
      typeof record.name === 'string'
        ? record.name
        : typeof record.path === 'string'
          ? record.path
          : 'file'
    const truncatedNote = record.truncated ? ' (truncated)' : ''
    return sanitizeAssistantVisibleContent(`Read ${label}${truncatedNote}:\n\n${record.content}`)
  }

  // GitHub tool reads return large text fields (file content, unified diff,
  // branch comparison, commit metadata, code-search snippets) that the generic
  // structured summarizer drops — the model saw only "Fields: ...". Surface
  // these verbatim (bounded) so get_file / get_pull_request_diff / compare_refs
  // / list_commits / search_code are actually usable.
  if (toolName === 'github' || toolName.startsWith('github__')) {
    const githubText = surfaceGithubReadResult(record)
    if (githubText) return sanitizeAssistantVisibleContent(githubText)
  }

  // Knowledge graph reads (queryKnowledgeGraph / relatedItems) return node
  // lists and edge-grouped related items whose sourceId/summary and nested
  // items the generic summarizer would drop. Surface them verbatim (bounded).
  if (toolName === 'knowledgeGraph' || toolName.startsWith('knowledgeGraph__')) {
    const graphText = surfaceKnowledgeGraphResult(record)
    if (graphText) return sanitizeAssistantVisibleContent(graphText)
  }

  const sections = summarizeStructuredResult(record)
  if (sections.length > 0) {
    return [`${toolName} returned:`, ...sections, MODEL_DETAIL_MESSAGE].join('\n')
  }

  const keys = Object.keys(record)
  const keySummary = keys.length > 0 ? ` Fields: ${keys.join(', ')}.` : ''

  return `${toolName} returned structured data.${keySummary} ${MODEL_DETAIL_MESSAGE}`
}
